/**
 * Small helpers used by MCPTTClient — base64/GUID coding, device-ID
 * persistence. Browser-only (no Node Buffer).
 */

import type { Base64Guid } from "./types";

/* ---------- Base64 ↔ Uint8Array ---------- */

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

export function base64ToUint8Array(b64: string): Uint8Array {
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/* ---------- Base64 GUID generation ---------- */

/** Random 16 bytes → base64. Manager's universal ID format. */
export function getBase64ID(): Base64Guid {
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return uint8ArrayToBase64(buf);
}

/** Persistent per-installation device ID (lives in localStorage). */
export function getDeviceID(): Base64Guid {
  const key = "DeviceID";
  let id: string | null = null;
  try { id = localStorage.getItem(key); } catch { /* private mode */ }
  if (id) return id;
  id = getBase64ID();
  try { localStorage.setItem(key, id); } catch { /* private mode */ }
  return id;
}

/** Convert base64 GUID to "01234567-89ab-cdef-..." dashed form (display only). */
export function base64ToGuid(b64: Base64Guid): string {
  const b = base64ToUint8Array(b64);
  if (b.length < 16) return b64;
  const h = (i: number) => b[i].toString(16).padStart(2, "0");
  return (
    h(3) + h(2) + h(1) + h(0) + "-" +
    h(5) + h(4) + "-" +
    h(7) + h(6) + "-" +
    h(8) + h(9) + "-" +
    h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
  );
}

/** Permission bitmap test that handles >32-bit values via BigInt. */
export function hasPermission(
  bitmap: number | bigint | undefined,
  perm: number | bigint
): boolean {
  if (bitmap == null) return false;
  const a = typeof bitmap === "bigint" ? bitmap : BigInt(bitmap);
  const b = typeof perm   === "bigint" ? perm   : BigInt(perm);
  return (a & b) === b;
}

/** Generic ID lookup over an array of objects keyed by `field` (default "ID"). */
export function findById<T extends Record<string, unknown>>(
  arr: T[] | undefined,
  id: unknown,
  field: keyof T = "ID" as keyof T
): T | undefined {
  if (!arr) return undefined;
  return arr.find((x) => x[field] === id);
}

export function indexById<T extends Record<string, unknown>>(
  arr: T[] | undefined,
  id: unknown,
  field: keyof T = "ID" as keyof T
): number {
  if (!arr) return -1;
  return arr.findIndex((x) => x[field] === id);
}
